import { useState, useRef } from 'react';
import { Upload, FileSpreadsheet, Loader2, AlertTriangle, CheckCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLUMN_MAP = {
  name: ['name', 'product name', 'title', 'product'],
  description: ['description', 'desc', 'details'],
  category: ['category', 'type', 'metal', 'metal type'],
  weight: ['weight', 'weight (oz)', 'oz', 'troy oz'],
  mint: ['mint', 'manufacturer', 'issuer', 'brand'],
  purity: ['purity', 'fineness', 'fine'],
  premium_percentage: ['premium', 'premium %', 'premium_percentage', 'markup'],
  image_url: ['image', 'image url', 'image_url', 'photo'],
  sort_order: ['sort', 'sort order', 'sort_order', 'order'],
};

function matchColumn(header) {
  const h = header.toLowerCase().trim();
  for (const [field, aliases] of Object.entries(COLUMN_MAP)) {
    if (aliases.includes(h)) return field;
  }
  return null;
}

function inferCategory(text) {
  const t = text.toLowerCase();
  if (t.includes('platinum')) return 'platinum';
  if (t.includes('gold')) return 'gold';
  if (t.includes('silver')) return 'silver';
  return 'gold';
}

function parseRow(row, headerMap) {
  const get = (field) => {
    const col = headerMap[field];
    return col !== undefined ? row[col] : '';
  };

  const rawName = String(get('name') || '').trim();
  if (!rawName) return null;

  const cat = String(get('category') || '').trim().toLowerCase();
  const category = ['gold', 'silver', 'platinum'].includes(cat) ? cat : inferCategory(rawName);

  return {
    name: rawName,
    description: String(get('description') || rawName).trim(),
    category,
    weight: parseFloat(get('weight')) || 1,
    weight_unit: (() => {
      const u = String(get('weight_unit') || 'oz').trim().toLowerCase();
      return ['oz', 'oz_avdp', 'g', 'kg'].includes(u) ? u : 'oz';
    })(),
    mint: String(get('mint') || '').trim(),
    purity: String(get('purity') || '').trim(),
    premium_percentage: parseFloat(get('premium_percentage')) || 5,
    image_url: String(get('image_url') || '').trim(),
    sort_order: parseInt(get('sort_order')) || 0,
    status: 'active',
    featured: false,
  };
}

export default function BulkImport({ onComplete }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [headerMap, setHeaderMap] = useState({});
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const fileRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

        if (data.length < 2) {
          toast.error('File must have headers and at least one data row');
          return;
        }

        const hdrs = data[0].map(h => String(h).trim());
        setHeaders(hdrs);

        const map = {};
        hdrs.forEach((h, i) => {
          const field = matchColumn(h);
          if (field) map[field] = i;
        });
        setHeaderMap(map);

        const parsed = data.slice(1)
          .map(r => parseRow(r, map))
          .filter(Boolean);

        setRows(parsed);
        setResults(null);
        setOpen(true);
      } catch {
        toast.error('Failed to parse file');
      }
    };
    reader.readAsArrayBuffer(file);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleImport = async () => {
    setImporting(true);
    const res = { success: 0, failed: 0, errors: [] };

    for (let i = 0; i < rows.length; i++) {
      try {
        await axios.post(`${API}/admin/products`, rows[i]);
        res.success++;
      } catch (err) {
        res.failed++;
        res.errors.push(`Row ${i + 1} (${rows[i].name}): ${err.response?.data?.detail || 'Failed'}`);
      }
    }

    setResults(res);
    setImporting(false);
    if (res.success > 0) {
      toast.success(`Imported ${res.success} product${res.success > 1 ? 's' : ''}`);
      onComplete?.();
    }
  };

  const removeRow = (idx) => setRows(rows.filter((_, i) => i !== idx));

  return (
    <>
      <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFile} className="hidden" data-testid="bulk-file-input" />
      <Button onClick={() => fileRef.current?.click()} variant="outline" className="text-sm" data-testid="bulk-import-btn">
        <FileSpreadsheet size={16} className="mr-2" /> Import CSV/XLSX
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="bulk-import-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Poppins', sans-serif" }}>
              Bulk Import Products ({rows.length} rows)
            </DialogTitle>
          </DialogHeader>

          {!headerMap.name && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-sm text-sm text-amber-700">
              <AlertTriangle size={16} />
              No "Name" column detected. Make sure your spreadsheet has a column named "Name" or "Product Name".
            </div>
          )}

          <div className="text-xs text-gray-400 mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Mapped columns: {Object.entries(headerMap).map(([f, i]) => `${f} → "${headers[i]}"`).join(', ') || 'None'}
          </div>

          {results && (
            <div className={`p-3 rounded-sm border text-sm ${results.failed ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle size={16} className="text-emerald-600" />
                <span>{results.success} imported successfully</span>
                {results.failed > 0 && <span className="text-amber-600">| {results.failed} failed</span>}
              </div>
              {results.errors.map((e, i) => <p key={`import-err-${i}-${e.slice(0, 20)}`} className="text-xs text-red-500 ml-6">{e}</p>)}
            </div>
          )}

          <div className="border rounded-sm overflow-auto max-h-[50vh]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">#</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Weight</TableHead>
                  <TableHead>Mint</TableHead>
                  <TableHead>Purity</TableHead>
                  <TableHead>Premium</TableHead>
                  <TableHead className="w-8"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={`${r.name}-${i}`}>
                    <TableCell className="text-xs text-gray-400">{i + 1}</TableCell>
                    <TableCell className="text-sm font-medium">{r.name}</TableCell>
                    <TableCell className="text-sm capitalize">{r.category}</TableCell>
                    <TableCell className="text-sm">{r.weight} {r.weight_unit || 'oz'}</TableCell>
                    <TableCell className="text-sm">{r.mint || '-'}</TableCell>
                    <TableCell className="text-sm">{r.purity || '-'}</TableCell>
                    <TableCell className="text-sm">{r.premium_percentage}%</TableCell>
                    <TableCell>
                      <button onClick={() => removeRow(i)} className="text-gray-300 hover:text-red-500"><X size={14} /></button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="bulk-cancel">Cancel</Button>
            <Button onClick={handleImport} disabled={importing || !rows.length} className="btn-gold" data-testid="bulk-confirm">
              {importing ? <><Loader2 size={16} className="mr-2 animate-spin" /> Importing...</> : `Import ${rows.length} Products`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
